import React from 'react'
import { Link } from 'react-router-dom'
import shallow from 'zustand/shallow'

import BusyOverlay from 'components/common/BusyOverlay'
import Button, { LinkButton } from 'components/common/Button'
import { Column } from 'components/common/Grid'
import Text from 'components/common/Text'
import TrackedExternalLink from 'components/common/TrackedExternalLink'
import CardWrapper from 'components/dashboard/CardWrapper'
import Icon from 'components/Icon'
import { useServicesStore } from 'hooks/use-services'
import theme from 'theme'
import { UserDefinedService } from 'types/api/services/service'

const ServiceDashboardRow: React.FC<{
  service: UserDefinedService
}> = ({ service }) => {
  const { name } = service

  return (
    <Link
      css={{
        display: 'grid',
        gridTemplateColumns: 'repeat(16, 1fr)',
        gap: theme.space[2],
        alignItems: 'center',
        borderBottom: theme.border.base,
        paddingTop: theme.space[3],
        paddingRight: theme.space[2],
        paddingBottom: theme.space[3],
        paddingLeft: theme.space[2],
        '&:hover': {
          color: theme.colors.steel[2],
          background: theme.colors.grayOverlay[0]
        }
      }}
      state={{
        referrer: '/',
        service
      }}
      to={`/services/${service.serviceId}`}
    >
      <Column
        css={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr)',
          gap: theme.space[2]
        }}
        span={15}
      >
        <Text as="h4" color="medium">
          {name}
        </Text>
      </Column>
      <Column
        css={{
          justifySelf: 'center'
        }}
        span={1}
      >
        <Icon
          css={{
            marginLeft: theme.space[2],
            color: theme.colors.steel[2]
          }}
          icon="chevronRight"
        />
      </Column>
    </Link>
  )
}

export const ServicesCard: React.FC = () => {
  const { allServices, status } = useServicesStore(
    (state) => ({ allServices: state.services, status: state.status }),
    shallow
  )

  const services = React.useMemo(() => {
    return Object.entries(allServices || {}).map(([, v]) => v)
  }, [allServices])

  return (
    <CardWrapper
      hideFilter
      renderCardContent={() => {
        if (status === 'loading') {
          return <BusyOverlay rounded shown />
        }
        if (services.length === 0) {
          return (
            <div
              css={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr)',
                gridTemplateRows: 'min-content',
                alignContent: 'center',
                height: '100%',
                padding: theme.space[3],
                gap: theme.space[3]
              }}
            >
              <Text as="h2" color="medium">
                Automatically discover your services
              </Text>
              <Text as="p" color="dark">
                Uncover the unknowns and weak links in your environment. Spend
                less time matching VMs and containers to services.
              </Text>
              <Button
                as={TrackedExternalLink}
                css={{ justifySelf: 'start' }}
                href="https://www.gremlin.com/docs/infrastructure-layer/services/"
              >
                Enable services discovery
              </Button>
            </div>
          )
        }

        return (
          <div>
            {services.map((service) => {
              return (
                <ServiceDashboardRow
                  key={service.serviceId}
                  service={service}
                />
              )
            })}
            <div
              css={{
                display: 'flex',
                justifyContent: 'center',
                padding: theme.space[2],
                width: '100%'
              }}
            >
              <LinkButton
                secondary
                loading={status === 'loading'}
                to="/services"
              >
                View All
              </LinkButton>
            </div>
          </div>
        )
      }}
      title="Services"
    />
  )
}
